import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Paperclip } from 'lucide-react';
import { Link, useMatch, useNavigate } from 'react-router';
import { queryKeys } from '../../lib/queryKeys';
import { cx } from '../../lib/cx';
import { useAuth } from '../auth/useAuth';
import { PendingStrip } from '../attachments/AttachmentStrip';
import { useUploader } from '../attachments/useUploader';
import { Composer, type ComposerHandle } from '../composer/Composer';
import { ReviewList } from '../review/ReviewList';
import { useCapabilities } from '../workspace/useCapability';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { createThread } from './api';

/**
 * Placeholder openers, picked at random per mount.
 *
 * The box has no fields, so its placeholder is the only thing showing what
 * belongs in it — and it now takes anything, a question as much as a bug, so
 * the examples mix both. A short list means the same line every other visit.
 */
const PLACEHOLDERS: readonly string[] = [
  'The map forgets my filters every time I blink…',
  'Are we still on for Thursday’s release?',
  'Can the quotes report stop hiding the third column?',
  'Photos upload sideways. Only sometimes. Possibly only on Tuesdays…',
  'Quick one — who is looking at the booking emails?',
  'The invoice PDF has strong opinions about page breaks…',
  'Two dashboard numbers disagree and I trust neither…',
  'Make the checkout button less shy…',
  'Every date on this page is one day out. Possibly two…',
  'The form says “saved” and then quietly disagrees…',
  'Is the new calendar view in this sprint?',
  'It loads. Eventually. Around the time I’ve given up…',
];

function randomPlaceholder(): string {
  return PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)] ?? PLACEHOLDERS[0]!;
}

function greetingFor(name: string | null): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const first = name?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

function today(): string {
  return new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(),
  );
}

/**
 * Starting a thread.
 *
 * Posting creates it at once and opens it — nothing waits on a model. If what
 * was written is work, @rundown picks it up in the thread a moment later and
 * files it; if it is conversation, it stays one. Staff can start a thread the
 * client never sees, for talk (or work) that is internal.
 *
 * On the landing route (not /new) the staff Review queue sits under the box,
 * where it was before Home became a thread list: "what came in, and what needs
 * a decision" is still the first question of the day.
 */
export function NewChatPane() {
  const { workspace } = useRequiredWorkspace();
  const capabilities = useCapabilities();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onNewRoute = useMatch('/new') !== null;

  const composerRef = useRef<ComposerHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [composerEmpty, setComposerEmpty] = useState(true);
  const [internal, setInternal] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [placeholder] = useState(randomPlaceholder);
  const uploader = useUploader(workspace.id);

  const canCreate = capabilities.has('request.create');
  const canBeInternal = capabilities.has('internal.read');
  const canReview = capabilities.has('request.review');

  const send = useMutation({
    mutationFn: (body: unknown) =>
      createThread(workspace.id, {
        body,
        attachmentIds: uploader.attachmentIds,
        internal: canBeInternal ? internal : undefined,
      }),
    onSuccess: (thread) => {
      composerRef.current?.clear();
      uploader.clear();
      setInternal(false);
      queryClient.setQueryData(queryKeys.thread(workspace.id, thread.id), thread);
      void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspace.id) });
      void navigate(`/t/${thread.id}`);
    },
  });

  const hasContent = !composerEmpty || uploader.attachmentIds.length > 0;
  const canPost = canCreate && hasContent && !send.isPending && !uploader.isUploading;

  const submit = () => {
    if (!canPost) return;
    const body = composerRef.current?.getContent();
    if (body === undefined || body === null) return;
    send.mutate(body);
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="animate-enter mx-auto w-full max-w-[52rem] px-5 pt-8 pb-14 md:px-8 md:pt-10">
        {onNewRoute && (
          <Link to="/" className="mb-3 inline-block fs-12 text-muted hover:text-ink md:hidden">
            ← Threads
          </Link>
        )}
        <div className="fs-12 text-muted">{today()}</div>
        <h1 className="mt-1.5 mb-6 fs-30 leading-tight font-bold tracking-[-0.02em] text-ink">
          {greetingFor(user?.name ?? null)}
        </h1>

        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            setDragging(false);
            uploader.handleDrop(event);
          }}
          className={cx(
            'rounded-[14px] border p-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_30px_-14px_rgba(0,0,0,0.12)]',
            dragging
              ? 'border-accent bg-accent-tint-soft'
              : internal
                ? 'border-violet'
                : 'border-line-strong',
          )}
        >
          <div className="px-2 pt-2">
            <Composer
              ref={composerRef}
              size="ask"
              workspaceId={workspace.id}
              ariaLabel="Start a thread"
              placeholder={placeholder}
              onSubmit={submit}
              onEmptyChange={setComposerEmpty}
              onPasteFiles={(event) => uploader.handlePaste(event)}
              onUpload={(files) => uploader.addFiles(files)}
            />
          </div>

          <PendingStrip uploads={uploader.uploads} onRemove={uploader.remove} />

          <div className="flex flex-wrap items-center gap-2 px-2 pt-1.5 pb-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                void uploader.addFiles([...(event.target.files ?? [])]);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={send.isPending}
              aria-label="Attach a file"
              title="Attach a file"
              className="rounded-lg p-1.5 text-muted hover:bg-surface-3 hover:text-ink disabled:opacity-40"
            >
              <Paperclip size={15} strokeWidth={1.75} aria-hidden />
            </button>
            {canBeInternal && (
              <label
                className={cx(
                  'flex cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1 fs-11.5',
                  internal ? 'bg-violet-tint font-semibold text-violet' : 'text-muted hover:text-ink',
                )}
                title="Only your team can see an internal thread"
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={internal}
                  onChange={(event) => setInternal(event.target.checked)}
                />
                <Lock size={12} strokeWidth={2} aria-hidden />
                Internal
              </label>
            )}
            <span className="hidden px-0.5 fs-11 text-faint sm:inline">
              Mention <span className="font-semibold">@rundown</span> to ask it something.
            </span>
            <button
              type="button"
              onClick={submit}
              disabled={!canPost}
              className="ml-auto rounded-lg bg-ink px-4 py-2 fs-13 font-semibold whitespace-nowrap text-white disabled:opacity-40"
            >
              {uploader.isUploading ? 'Uploading…' : send.isPending ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>

        {send.error !== null && (
          <p role="alert" className="mt-3 rounded-lg bg-danger-tint px-3 py-2 fs-12 text-danger">
            Could not post that. {send.error.message}
          </p>
        )}

        <p className="mt-3 fs-12 text-muted">
          Post anything — a question, a bug, an idea. It opens as a chat straight away. If it is something to
          work on, @rundown files it as an issue in the thread; if it missed one, say “@rundown analyse this”.
        </p>

        {canReview && !onNewRoute && (
          <section className="mt-10" aria-labelledby="needs-review-heading">
            <h2
              id="needs-review-heading"
              className="mb-2.5 fs-11 font-bold tracking-[0.06em] text-muted uppercase"
            >
              Needs review
            </h2>
            <ReviewList />
          </section>
        )}
      </div>
    </div>
  );
}
